<template>
  <el-dialog
    v-model="dialogVisible"
    :title="translate('Welcome — would you like a tour?')"
    width="420px"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    :z-index="100000"
    align-center
  >
    <p v-if="eligibleTour">
      {{ translate(eligibleTour.title) }}
    </p>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="onNotNow">{{ translate('Not now') }}</el-button>
        <el-button type="primary" @click="onYes">{{ translate('Yes, show me') }}</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script>
/* eslint-disable */
import { mapGetters, mapState } from 'vuex'
import registry from '../definitions/_registry'

export default {
  name: 'CcWelcomeModal',

  data() {
    return {
      // Per-page-load guard: even if we re-fetch tours, don't re-open the modal.
      dismissed: false,
    }
  },

  computed: {
    ...mapGetters(['isShopFloorWorker']),
    ...mapState({
      tours: (state) => state.Tours.tours,
      progress: (state) => state.Tours.progress,
      skipAutoStart: (state) => state.Tours.skipAutoStart,
      fetched: (state) => state.Tours.fetched,
      activeTourKey: (state) => state.Tours.activeTourKey,
    }),

    // Infer whether the current user has the Admin claim. The backend already filters
    // tours by audience (only Admins receive any audience='Admin' tour), so the presence
    // of any Admin-audience tour in our list is a reliable Admin signal.
    isAdminUser() {
      return (this.tours || []).some(n => (n.audience || '').toLowerCase() === 'admin')
    },

    // First eligible auto-start tour that the user has never touched.
    // For non-Admin users, prefer audience='Manager' tours over audience='Admin,Manager' tours;
    // within each group, sort by displayOrder. For Admins, simple displayOrder sort.
    eligibleTour() {
      if (!this.fetched || this.skipAutoStart) return null
      if (this.isShopFloorWorker) return null  // shopfloor uses direct auto-start in main.vue, not the welcome modal
      const progressKeys = new Set((this.progress || []).map(n => n.tourKey))
      let candidates = (this.tours || [])
        .filter(n => n.isAutoStartEnabled && n.isActive && !progressKeys.has(n.key))
        .filter(n => !!registry[n.key])  // require a FE definition

      // Chain: M5 only surfaces in welcome modal once A1 is completed (manual launch via ? menu still works).
      const a1Completed = (this.progress || []).some(n => n.tourKey === 'A1' && n.status === 'Completed')
      const requiresA1 = (key) => key === 'M5'
      candidates = candidates.filter(n => !requiresA1(n.key) || a1Completed)

      const sorted = this.isAdminUser
        ? candidates.slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
        : candidates.slice().sort((a, b) => {
            const rank = (t) => (t.audience || '').toLowerCase() === 'manager' ? 0 : 1
            const r = rank(a) - rank(b)
            if (r !== 0) return r
            return (a.displayOrder || 0) - (b.displayOrder || 0)
          })

      return sorted.length > 0 ? sorted[0] : null
    },

    dialogVisible: {
      get() {
        return !this.dismissed
          && !!this.eligibleTour
          && this.activeTourKey == null
      },
      set(value) {
        if (!value) this.dismissed = true
      },
    },
  },

  methods: {
    async onYes() {
      const tour = this.eligibleTour
      if (!tour) {
        this.dismissed = true
        return
      }
      const def = registry[tour.key]
      this.dismissed = true
      try {
        if (def && def.route && this.$route.path !== def.route) {
          await this.$router.push(def.route)
        }
        await this.$store.dispatch('Tours/startTour', tour.key)
      } catch (err) {
        // ignore — runner won't open if dispatch failed
      }
    },

    onNotNow() {
      this.dismissed = true
    },
  },
}
</script>

<style scoped>
.dialog-footer {
  display: inline-flex;
  gap: 8px;
}
</style>
